import { useState } from "react";
import { toast } from "sonner";
import { Send, Phone, ShieldCheck } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Reveal, Label } from "@/components/site/Motion";
import { useSettings } from "@/hooks/useSettings";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { SERVICES } from "@/data/content";

export default function HomeEnquiry() {
  const s = useSettings();
  const [form, setForm] = useState({ name: "", phone: "", service: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Please share your name and phone number.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/enquiries", form);
      toast.success("Thank you! Our team will call you shortly.");
      setForm({ name: "", phone: "", service: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="mx-auto max-w-7xl px-5 lg:px-8 py-24 lg:py-32" id="enquiry" data-testid="home-enquiry">
      <div className="grid gap-0 md:grid-cols-12 overflow-hidden rounded-sm border border-navy/10">
        <div className="relative md:col-span-5 min-h-[320px]">
          <img
            src="https://images.unsplash.com/photo-1632201147654-f6f54427e538?crop=entropy&cs=srgb&fm=jpg&q=85"
            alt="UYI construction site in India"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-navy-deep/70" />
          <div className="relative z-10 p-8 lg:p-10 h-full flex flex-col justify-between text-white">
            <div>
              <Label>Free consultation</Label>
              <h2 className="mt-5 font-display text-3xl lg:text-4xl font-light leading-tight">
                Get a free estimate for your project.
              </h2>
              <p className="mt-4 text-white/60 leading-relaxed text-sm">
                Share a few details and our Lucknow team will call you back with a
                transparent, no-obligation quote.
              </p>
            </div>
            <div className="mt-8 space-y-3 text-sm">
              <a href={`tel:${(s.phone_primary || "").replace(/\s/g, "")}`} className="flex items-center gap-3 text-white/90 hover:text-gold transition-colors">
                <Phone className="h-4 w-4 text-gold" /> {s.phone_primary}
              </a>
              <div className="flex items-center gap-3 text-white/60">
                <ShieldCheck className="h-4 w-4 text-gold" /> {s.years_experience} years · {s.rating}★ Google rating
              </div>
            </div>
          </div>
        </div>

        <div className="md:col-span-7 bg-white p-8 lg:p-12">
          <form onSubmit={submit} data-testid="home-enquiry-form" className="grid gap-5 sm:grid-cols-2">
            <label className="block">
              <span className="mb-2 block text-xs uppercase tracking-[0.15em] text-navy/50">Full name *</span>
              <Input data-testid="home-name" value={form.name} onChange={set("name")} placeholder="Your name" className="rounded-sm" />
            </label>
            <label className="block">
              <span className="mb-2 block text-xs uppercase tracking-[0.15em] text-navy/50">Phone *</span>
              <Input data-testid="home-phone" value={form.phone} onChange={set("phone")} placeholder="+91" className="rounded-sm" />
            </label>
            <label className="block sm:col-span-2">
              <span className="mb-2 block text-xs uppercase tracking-[0.15em] text-navy/50">Service</span>
              <select data-testid="home-service" value={form.service} onChange={set("service")} className="h-10 w-full rounded-sm border border-input bg-background px-3 text-sm text-navy">
                <option value="">Select a service</option>
                {SERVICES.map((sv) => (
                  <option key={sv.title} value={sv.title}>{sv.title}</option>
                ))}
              </select>
            </label>
            <label className="block sm:col-span-2">
              <span className="mb-2 block text-xs uppercase tracking-[0.15em] text-navy/50">Message</span>
              <Textarea data-testid="home-message" value={form.message} onChange={set("message")} rows={3} placeholder="Tell us about your plot / space / idea…" className="rounded-sm" />
            </label>
            <button
              type="submit"
              data-testid="home-enquiry-submit"
              disabled={loading}
              className="sm:col-span-2 inline-flex items-center justify-center gap-3 rounded-sm bg-navy px-8 py-4 text-white hover:bg-gold hover:text-navy transition-colors duration-300 disabled:opacity-60"
            >
              {loading ? "Sending…" : "Request a call back"} <Send className="h-4 w-4" />
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}
